import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Mail, Server, Check, AlertCircle, Save, Loader2, PlayCircle, Key, User, ExternalLink, ChevronDown, ChevronUp, HelpCircle, Info, Eye, EyeOff } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../../../components/ui/card';
import { Button } from '../../../../../components/ui/button';
import { Input } from '../../../../../components/ui/input';
import { Label } from '../../../../../components/ui/label';
import { Switch } from '../../../../../components/ui/switch';

const EmailIntegration = ({
    chatbotId,
    emailIntegrations = [], // Array of config objects
    setEmailIntegrations, // Function to update the array
    persistAndUpdate
}) => {
    // We'll manage the "active" integration being edited.
    // For now, we'll assume single integration (index 0) but support array structure for future.
    const [activeConfig, setActiveConfig] = useState(emailIntegrations[0] || {
        id: 'default',
        name: 'Default Email',
        host: '',
        port: 587,
        secure: false,
        user: '',
        pass: '',
        fromName: '',
        fromEmail: ''
    });

    // Sync state with props when data is loaded
    useEffect(() => {
        if (emailIntegrations && emailIntegrations.length > 0) {
            setActiveConfig(emailIntegrations[0]);
        }
    }, [emailIntegrations]);

    const [testStatus, setTestStatus] = useState(null); // 'testing', 'success', 'error'
    const [testMessage, setTestMessage] = useState('');
    const [showPassword, setShowPassword] = useState(false);

    // UI States
    const [showTroubleshooting, setShowTroubleshooting] = useState(false);
    const [activeTab, setActiveTab] = useState('microsoft365'); // 'microsoft365', 'outlook', 'gmail'

    const handleUpdate = (field, value) => {
        setActiveConfig(prev => ({
            ...prev,
            [field]: value
        }));
    };



    const handleTestConnection = async () => {
        setTestStatus('testing');
        setTestMessage('');

        const isSystemDefault = !activeConfig.host || !activeConfig.user;
        const payload = isSystemDefault ? { provider: 'ses' } : activeConfig;

        try {
            const response = await fetch('/api/integrations/email/test', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            const data = await response.json();

            if (response.ok && data.success) {
                setTestStatus('success');
                setTestMessage('Connection successful!');
            } else {
                setTestStatus('error');
                // Log full details for debugging (shows ESOCKET, ETIMEDOUT, etc.)
                console.error('SMTP Test Failed:', data);
                setTestMessage(data.error || 'Connection failed');
            }
        } catch (error) {
            setTestStatus('error');
            setTestMessage('Network error occurred');
        }
    };

    const handleSave = () => {
        // Update the parent state array
        // For MVP phase 1, we just replace/set index 0
        const newIntegrations = [activeConfig];
        setEmailIntegrations(newIntegrations);
        persistAndUpdate({ emailIntegrations: newIntegrations });
    };



    return (
        <div className="space-y-6">
            <Card className="w-full shadow-sm border-gray-200">
                <CardHeader className="pb-4 border-b border-gray-100">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-blue-50 rounded-lg">
                                <Mail className="w-6 h-6 text-blue-600" />
                            </div>
                            <div>
                                <CardTitle className="text-lg font-semibold text-gray-900">Email Configuration</CardTitle>
                                <CardDescription className="text-sm text-gray-500">
                                    Configure how your chatbot sends email notifications
                                </CardDescription>
                            </div>
                        </div>
                        <div className="px-3 py-1 bg-gray-100 rounded-full text-xs font-medium text-gray-600 capitalize">
                            {activeConfig.host && activeConfig.user ? 'SMTP' : 'Platform Default'}
                        </div>
                    </div>
                </CardHeader>
                <CardContent className="pt-6 space-y-6">
                    <div className="bg-blue-50 p-4 rounded-lg flex items-start gap-3 border border-blue-100">
                        <Info className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                        <div className="text-sm text-blue-800">
                            <p className="font-medium mb-1">Email Delivery</p>
                            <p>
                                By default, notifications are sent via the SmushChat platform email service.
                                You can optionally configure your own SMTP server if you prefer emails to come from your own domain.
                            </p>
                        </div>
                    </div>

                    <div className="space-y-6 animate-in fade-in slide-in-from-top-4 duration-300">
                        <div className="flex justify-end gap-2">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                    handleUpdate('host', 'smtp.gmail.com');
                                    handleUpdate('port', 465);
                                    handleUpdate('secure', true);
                                    handleUpdate('provider', 'smtp');
                                }}
                            >
                                Gmail
                            </Button>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                    handleUpdate('host', 'smtp.office365.com');
                                    handleUpdate('port', 587);
                                    handleUpdate('secure', false);
                                    handleUpdate('provider', 'smtp');
                                }}
                            >
                                Outlook
                            </Button>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label htmlFor="host">SMTP Host</Label>
                                <div className="relative">
                                    <Server className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                                    <Input
                                        id="host"
                                        value={activeConfig.host || ''}
                                        onChange={(e) => {
                                            handleUpdate('host', e.target.value);
                                            handleUpdate('provider', 'smtp');
                                        }}
                                        placeholder="smtp.gmail.com"
                                        className="pl-9"
                                    />
                                </div>
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="port">Port</Label>
                                <Input
                                    id="port"
                                    value={activeConfig.port || ''}
                                    onChange={(e) => handleUpdate('port', e.target.value)}
                                    placeholder="465 or 587"
                                />
                            </div>
                        </div>

                        <div className="flex items-center space-x-2">
                            <Switch
                                id="secure"
                                checked={activeConfig.secure || false}
                                onCheckedChange={(checked) => handleUpdate('secure', checked)}
                            />
                            <Label htmlFor="secure" className="font-medium">Use Secure Connection (SSL/TLS)</Label>
                        </div>
                        <p className="text-xs text-gray-500 ml-12 -mt-1">
                            Typically enabled (true) for port 465, disabled (false) for 587.
                        </p>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label htmlFor="user">Username / Email</Label>
                                <div className="relative">
                                    <User className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                                    <Input
                                        id="user"
                                        value={activeConfig.user || ''}
                                        onChange={(e) => handleUpdate('user', e.target.value)}
                                        placeholder="email@example.com"
                                        className="pl-9"
                                    />
                                </div>
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="pass">Password / App Password</Label>
                                <div className="relative">
                                    <Key className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                                    <Input
                                        id="pass"
                                        type={showPassword ? "text" : "password"}
                                        value={activeConfig.pass || ''}
                                        onChange={(e) => handleUpdate('pass', e.target.value)}
                                        placeholder="••••••••••••••••"
                                        className="pl-9 pr-10"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute right-3 top-3 text-gray-400 hover:text-gray-600 focus:outline-none"
                                    >
                                        {showPassword ? (
                                            <EyeOff className="h-4 w-4" />
                                        ) : (
                                            <Eye className="h-4 w-4" />
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label htmlFor="fromName">From Name (Optional)</Label>
                                <Input
                                    id="fromName"
                                    value={activeConfig.fromName || ''}
                                    onChange={(e) => handleUpdate('fromName', e.target.value)}
                                    placeholder="Support Team"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="fromEmail">From Email (Optional)</Label>
                                <Input
                                    id="fromEmail"
                                    value={activeConfig.fromEmail || ''}
                                    onChange={(e) => handleUpdate('fromEmail', e.target.value)}
                                    placeholder="Same as username"
                                />
                            </div>
                        </div>

                        <div className="bg-gray-50 p-4 rounded-lg">
                            <button
                                className="flex items-center justify-between w-full text-sm font-medium text-gray-700"
                                onClick={() => setShowTroubleshooting(!showTroubleshooting)}
                            >
                                <div className="flex items-center gap-2">
                                    <HelpCircle className="w-4 h-4 text-slate-500" />
                                    <span className="text-sm font-semibold text-slate-700">Trouble connecting? View setup guides</span>
                                </div>
                                {showTroubleshooting ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                            </button>

                            {showTroubleshooting && (
                                <div className="mt-4 pt-4 border-t border-gray-200">
                                    {/* Tabs */}
                                    <div className="flex border-b border-gray-200 bg-gray-50/50">
                                        <button
                                            onClick={() => setActiveTab('microsoft365')}
                                            className={`flex-1 py-2 text-xs font-bold uppercase tracking-wide border-b-2 transition-colors ${activeTab === 'microsoft365'
                                                ? 'border-blue-600 text-blue-700 bg-blue-50/50'
                                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                                                } `}
                                        >
                                            Microsoft 365
                                        </button>
                                        <button
                                            onClick={() => setActiveTab('outlook')}
                                            className={`flex-1 py-2 text-xs font-bold uppercase tracking-wide border-b-2 transition-colors ${activeTab === 'outlook'
                                                ? 'border-blue-600 text-blue-700 bg-blue-50/50'
                                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                                                } `}
                                        >
                                            Outlook Personal
                                        </button>
                                        <button
                                            onClick={() => setActiveTab('gmail')}
                                            className={`flex-1 py-2 text-xs font-bold uppercase tracking-wide border-b-2 transition-colors ${activeTab === 'gmail'
                                                ? 'border-blue-600 text-blue-700 bg-blue-50/50'
                                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                                                } `}
                                        >
                                            Gmail
                                        </button>
                                    </div>

                                    {/* Tab Content */}
                                    <div className="p-4 bg-white rounded-b-lg border border-t-0 border-gray-200">
                                        {activeTab === 'microsoft365' && (
                                            <div className="space-y-3 animate-in fade-in">
                                                <h5 className="text-xs font-bold text-gray-800">Microsoft 365 (Organization) Setup</h5>
                                                <ol className="list-decimal list-inside space-y-2 text-xs text-gray-600 ml-1">
                                                    <li>Go to <a href="https://mysignins.microsoft.com/security-info" target="_blank" rel="noopener noreferrer" className="font-semibold text-blue-600 hover:underline">My Account &gt; Security Info</a>.</li>
                                                    <li><span className="font-semibold text-gray-700">Enable 2FA/MFA</span> if not already on.</li>
                                                    <li>Add sign-in method &gt; Choose <span className="font-semibold text-gray-700">"App Password"</span>.</li>
                                                    <li>Name it "SmushChat" (or similar) and copy the generated password.</li>
                                                    <li>Use your full email address as Username and the App Password as Password.</li>
                                                    <li className="text-amber-600 italic mt-2 bg-amber-50 p-2 rounded border border-amber-100">
                                                        Note: Creating App Passwords requires your admin to disable "Security Defaults" in Azure AD.
                                                    </li>
                                                </ol>
                                                <a href="https://mysignins.microsoft.com/security-info" target="_blank" rel="noopener noreferrer" className="text-[10px] uppercase font-bold text-blue-600 hover:text-blue-700 hover:underline mt-2 inline-flex items-center gap-1">
                                                    Open Security Info <ExternalLink className="w-3 h-3" />
                                                </a>
                                            </div>
                                        )}

                                        {activeTab === 'outlook' && (
                                            <div className="space-y-3 animate-in fade-in">
                                                <h5 className="text-xs font-bold text-gray-800">Personal Outlook / Hotmail Setup</h5>
                                                <ol className="list-decimal list-inside space-y-2 text-xs text-gray-600 ml-1">
                                                    <li>Go to <a href="https://account.live.com/proofs/manage/additional" target="_blank" rel="noopener noreferrer" className="font-semibold text-blue-600 hover:underline">Microsoft Account &gt; Advanced Security</a>.</li>
                                                    <li>Scroll down to "Additional security options".</li>
                                                    <li>Ensure <span className="font-semibold text-gray-700">Two-step verification</span> is turned ON.</li>
                                                    <li>Look for "App passwords" section and clicking "Create a new app password".</li>
                                                    <li>Use this generated password instead of your main account password.</li>
                                                </ol>
                                                <a href="https://account.live.com/proofs/manage/additional" target="_blank" rel="noopener noreferrer" className="text-[10px] uppercase font-bold text-blue-600 hover:text-blue-700 hover:underline mt-2 inline-flex items-center gap-1">
                                                    Open Outlook Settings <ExternalLink className="w-3 h-3" />
                                                </a>
                                            </div>
                                        )}

                                        {activeTab === 'gmail' && (
                                            <div className="space-y-3 animate-in fade-in">
                                                <h5 className="text-xs font-bold text-gray-800">Google Workspace / Gmail Setup</h5>
                                                <ol className="list-decimal list-inside space-y-2 text-xs text-slate-600 ml-1">
                                                    <li>Go to <a href="https://myaccount.google.com/security" target="_blank" rel="noopener noreferrer" className="font-semibold text-blue-600 hover:underline">Google Account &gt; Security</a>.</li>
                                                    <li>Under "How you sign in to Google", enable <span className="font-semibold text-slate-700">2-Step Verification</span>.</li>
                                                    <li>Once enabled, go to the search bar at top and search for <span className="font-semibold text-slate-700">"App Passwords"</span>.</li>
                                                    <li>Create a new app password for "SmushChat".</li>
                                                    <li>Copy the 16-character code and use it as your password in the settings above.</li>
                                                </ol>
                                                <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noopener noreferrer" className="text-[10px] uppercase font-bold text-blue-600 hover:text-blue-700 hover:underline mt-2 inline-flex items-center gap-1">
                                                    Open Gmail App Passwords <ExternalLink className="w-3 h-3" />
                                                </a>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Actions Footer */}
            <div className="flex items-center justify-between pt-2">
                <div className="flex items-center gap-3">
                    <button
                        onClick={handleTestConnection}
                        disabled={testStatus === 'testing' || (activeConfig.host && !activeConfig.user)}
                        className={`px - 4 py - 2.5 rounded - xl text - xs font - bold tracking - wide transition - all flex items - center gap - 2 uppercase
                            ${testStatus === 'testing'
                                ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                                : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50 hover:text-slate-900 shadow-sm hover:shadow'
                            }
`}
                    >
                        {testStatus === 'testing' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <PlayCircle className="w-3.5 h-3.5" />}
                        {(!activeConfig.host || !activeConfig.user) ? 'Test System Email' : 'Test Connection'}
                    </button>

                    {testMessage && (
                        <div className={`flex items - center gap - 2 text - xs font - medium px - 3 py - 1.5 rounded - full animate -in fade -in zoom -in duration - 200 ${testStatus === 'success' ? 'bg-green-50 text-green-700 border border-green-100' : 'bg-red-50 text-red-700 border border-red-100'} `}>
                            {testStatus === 'success' ? <Check className="w-3 h-3" /> : <AlertCircle className="w-3 h-3" />}
                            {testMessage}
                        </div>
                    )}
                </div>

                <button
                    onClick={handleSave}
                    className="px-6 py-2.5 bg-slate-900 hover:bg-slate-800 text-white rounded-xl text-sm font-semibold shadow-lg shadow-slate-200 hover:shadow-xl transition-all flex items-center gap-2 transform active:scale-95"
                >
                    <Save className="w-4 h-4" />
                    Save Configurations
                </button>
            </div>
        </div>
    );
};

EmailIntegration.propTypes = {
    chatbotId: PropTypes.string,
    emailIntegrations: PropTypes.array,
    setEmailIntegrations: PropTypes.func.isRequired,
    persistAndUpdate: PropTypes.func.isRequired
};

export default EmailIntegration;
